import React, { useMemo, useState, useCallback } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Center,
  Circle,
  Divider,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import useAuditReport from './useAuditReport';
import { RepeatIcon, DownloadIcon } from '@chakra-ui/icons';
import { downloadAsExcel } from './downloadAsExcel';

export const AUDIT_REPORT_MIME = 'application/x-audit-report';

// ─── Provider Score Table ─────────────────────────────────────────────────────

const ProviderScoreTable = ({ rows, providerName, totalAudited, cqScore }) => {
  const flagged = rows.filter((r) => r.value > 0);
  const clean   = rows.filter((r) => r.value === 0);
  const allRows = [...flagged, ...clean];
  const RIDisplay = cqScore != null ? `${(cqScore).toFixed(0)}%` : '—';

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="simple" style={{ borderCollapse: 'collapse' }}>
        <Thead bg="gray.50">
          <Tr>
            <Th w="38%" fontSize="xs" color="gray.500" fontWeight="semibold">Audit Issue</Th>
            <Th isNumeric w="7%" fontSize="xs" color="gray.500" fontWeight="semibold">Value</Th>
            <Th fontSize="xs" color="gray.500" fontWeight="semibold">Comments</Th>
          </Tr>
        </Thead>
        <Tbody>
          {allRows.map((row) =>
            row.value > 0 ? (
              <Tr key={row.audit_issue} bg="orange.50">
                <Td fontWeight="semibold" color="orange.800" verticalAlign="top" fontSize="sm">
                  {row.audit_issue}
                </Td>
                <Td isNumeric verticalAlign="top">
                  <Badge colorScheme="orange">{row.value}</Badge>
                </Td>
                <Td verticalAlign="top">
                  {row.comments
                    ? row.comments.split('\n').map((line, i) => (
                        <Text key={i} fontSize="xs" color="gray.700" mb={1} lineHeight="1.6">
                          {line}
                        </Text>
                      ))
                    : null}
                </Td>
              </Tr>
            ) : (
              <Tr key={row.audit_issue}>
                <Td color="gray.500" fontSize="sm">{row.audit_issue}</Td>
                <Td isNumeric>
                  <Text color="gray.300" fontSize="sm">0</Text>
                </Td>
                <Td />
              </Tr>
            )
          )}
        </Tbody>
      </Table>

      <Box bg="gray.50" borderTop="1px" borderColor="gray.200" px={3} py={2}>
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr>
              <Th fontSize="xs" color="gray.500" fontWeight="semibold" w="38%">Provider</Th>
              <Th fontSize="xs" color="gray.500" fontWeight="semibold" w="15%">Total Audited</Th>
              <Th fontSize="xs" color="gray.500" fontWeight="semibold">RI score</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr bg="orange.100">
              <Td fontWeight="semibold" color="orange.800" fontSize="sm">{providerName}</Td>
              <Td fontWeight="semibold" color="orange.800" fontSize="sm">{totalAudited}</Td>
              <Td>
                <Badge colorScheme="yellow" fontSize="sm" px={3} py={1} borderRadius="sm" bg="yellow.300" color="orange.800">
                  {RIDisplay}
                </Badge>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

// ─── Provider Summary Panel ───────────────────────────────────────────────────

const MetricCard = ({ label, value, colorScheme = 'gray' }) => (
  <Box bg={`${colorScheme}.50`} borderRadius="md" p={3} textAlign="center" border="1px" borderColor={`${colorScheme}.100`} minW="0">
    <Text fontSize="xs" color={`${colorScheme}.600`} fontWeight="semibold" mb={1} noOfLines={1}>{label}</Text>
    <Text fontSize="lg" fontWeight="bold" color={`${colorScheme}.800`}>{value ?? '—'}</Text>
  </Box>
);

const ProviderSummaryPanel = ({ summary, providerName }) => {
  const score     = summary.cq_score;
  const cqDisplay = score != null ? `${(score * 100).toFixed(1)}%` : '—';

  return (
    <VStack align="stretch" spacing={4} p={4}>
      <Box>
        <Text fontSize="sm" fontWeight="bold" color="gray.800">Summary — Audit Notes: {providerName}</Text>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mt={1} mb={1}>Overall findings</Text>
        <Text fontSize="sm" color="gray.700">{summary.overall_findings}</Text>
      </Box>

      {summary.issues_flagged?.length > 0 && (
        <>
          <Divider />
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>Issues flagged</Text>
            <HStack flexWrap="wrap" spacing={2}>
              {summary.issues_flagged.map((issue) => (
                <Badge key={issue} colorScheme="orange" variant="subtle">{issue}</Badge>
              ))}
            </HStack>
          </Box>
        </>
      )}

      {summary.encounter_notes?.length > 0 && (
        <>
          <Divider />
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>Encounter-level findings</Text>
            <VStack align="stretch" spacing={2}>
              {summary.encounter_notes.map(({ encounter, note }) => (
                <Box key={encounter} p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.100">
                  <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={1}>Encounter #: {encounter}</Text>
                  <Text fontSize="xs" color="gray.700">{note}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </>
      )}
    </VStack>
  );
};

// ─── Main content ─────────────────────────────────────────────────────────────

export const AuditReportContent = ({ data, loading, error, onRefresh }) => {

  // ── CHANGED: flatten providers, injecting a sentinel for charts with errors ──
  const allProviders = useMemo(
    () =>
      data?.charts?.flatMap((c) =>
        c.error
          ? [{ _chartError: c.error, chart: c, provider_name: c.chart_id ?? 'Chart' }]
          : c.providers.map((p) => ({ ...p, chart: c }))
      ) ?? [],
    [data]
  );

  const [activeProvider, setActiveProvider] = useState(0);
  const [activeSubTab, setActiveSubTab]     = useState({});
  // ── CHANGED: track download in progress ──
  const [downloading, setDownloading]       = useState(false);

  const currentSubTab = activeSubTab[activeProvider] ?? 'score';
  const setSubTab     = useCallback(
    (tab) => setActiveSubTab((prev) => ({ ...prev, [activeProvider]: tab })),
    [activeProvider]
  );

  if (loading) {
    return (
      <Center h="100%" minH="300px" flexDirection="column" gap={3}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
        <Text color="gray.500" fontSize="sm">Generating audit report…</Text>
      </Center>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="semibold" fontSize="sm">Failed to load audit report</Text>
            <Text fontSize="xs" color="red.600" mt={1}>{error}</Text>
          </Box>
          {onRefresh && (
            <Circle size="32px" bg="red.50" color="red.500" cursor="pointer" onClick={onRefresh} _hover={{ bg: 'red.100' }} ml={2} title="Try again">
              <RepeatIcon boxSize="14px" />
            </Circle>
          )}
        </Alert>
      </Box>
    );
  }

  if (!allProviders.length) {
    return (
      <Center h="100%" minH="300px">
        <Text color="gray.400" fontSize="sm">No audit data is available. Please check later once the file is reviewed.</Text>
      </Center>
    );
  }

  const provider = allProviders[activeProvider];

  return (
    <Box h="100%" overflowY="auto" display="flex" flexDirection="column">

      <HStack px={4} pt={4} pb={3} justify="space-between" align="center" flexWrap="wrap" flexShrink={0}>
        <HStack spacing={2}>
          <Text fontSize="lg" fontWeight="bold" color="gray.800">Audit Report</Text>
          {data.batch_id && <Badge colorScheme="purple">Batch {data.batch_id}</Badge>}
          <Badge colorScheme="gray" variant="subtle">
            {data.total_charts} file{data.total_charts !== 1 ? 's' : ''}
          </Badge>
        </HStack>

        <HStack spacing={2}>
          {onRefresh && (
            <Tooltip label="Refresh report" placement="left">
              <Circle
                size="32px" border="1px" borderColor="gray.200" bg="white"
                color="gray.500" cursor={loading ? 'not-allowed' : 'pointer'}
                onClick={loading ? undefined : onRefresh}
                _hover={{ bg: 'gray.50', color: 'blue.500', borderColor: 'blue.200' }}
                transition="all 0.2s"
              >
                <RepeatIcon boxSize="14px" style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none', display: 'block' }} />
              </Circle>
            </Tooltip>
          )}

          {/* ── CHANGED: download button with loader ── */}
          <Tooltip label={downloading ? 'Preparing download…' : 'Download as Excel'} placement="left">
            <HStack
              as="button"
              spacing={1}
              px={3}
              h="32px"
              border="1px"
              borderColor={downloading ? 'green.300' : 'gray.200'}
              borderRadius="md"
              bg={downloading ? 'green.50' : 'white'}
              color={downloading ? 'green.700' : 'gray.600'}
              fontSize="sm"
              fontWeight="medium"
              cursor={downloading ? 'not-allowed' : 'pointer'}
              _hover={downloading ? {} : { bg: 'green.50', color: 'green.700', borderColor: 'green.200' }}
              transition="all 0.2s"
              onClick={async () => {
                if (downloading) return;
                setDownloading(true);
                try {
                  await downloadAsExcel(data);
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {downloading ? (
                <>
                  <Spinner size="xs" color="green.500" />
                  <Text fontSize="sm">Downloading…</Text>
                </>
              ) : (
                <>
                  <DownloadIcon boxSize="13px" />
                  <Text fontSize="sm">Download Report</Text>
                </>
              )}
            </HStack>
          </Tooltip>
        </HStack>
      </HStack>

      <Box px={4} flexShrink={0}>
        <HStack spacing={0} borderBottom="1px" borderColor="gray.200" overflowX="auto">
          {allProviders.map((p, i) => (
            <Box
              key={i} px={4} py={2} cursor="pointer" fontSize="sm"
              fontWeight={activeProvider === i ? 'semibold' : 'normal'}
              color={activeProvider === i ? 'blue.700' : 'gray.500'}
              borderBottom="2px"
              borderColor={activeProvider === i ? 'blue.600' : 'transparent'}
              bg={activeProvider === i ? 'blue.50' : 'transparent'}
              onClick={() => setActiveProvider(i)}
              _hover={{ color: 'blue.600', bg: 'blue.50' }}
              transition="all 0.15s"
              whiteSpace="nowrap"
              userSelect="none"
            >
              {/* ── CHANGED: show warning icon on tab if this is an error chart ── */}
              {p._chartError ? `⚠ ${p.provider_name}` : p.provider_name}
            </Box>
          ))}
        </HStack>
      </Box>

      <Box flex="1" overflow="hidden" display="flex" flexDirection="column">
        <HStack spacing={0} bg="blue.600" px={2} flexShrink={0}>
          {[
            { key: 'score',   label: 'Provider Score'   },
            { key: 'summary', label: 'Provider Summary' },
          ].map(({ key, label }) => (
            <Box
              key={key} px={4} py={2} cursor="pointer" fontSize="xs" fontWeight="semibold"
              color={currentSubTab === key ? 'white' : 'blue.200'}
              borderBottom="2px"
              borderColor={currentSubTab === key ? 'white' : 'transparent'}
              onClick={() => setSubTab(key)}
              _hover={{ color: 'white' }}
              transition="all 0.15s"
              userSelect="none"
            >
              {label}
            </Box>
          ))}
        </HStack>

        <Box flex="1" overflowY="auto">
          {/* ── CHANGED: show error alert if this chart used old logic ── */}
          {provider._chartError ? (
            <Box p={6}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold" fontSize="sm">Report not available for this chart</Text>
                  <Text fontSize="xs" color="orange.700" mt={1}>{provider._chartError}</Text>
                </Box>
              </Alert>
            </Box>
          ) : currentSubTab === 'score' ? (
            <ProviderScoreTable
              rows={provider.provider_score.rows}
              providerName={provider.provider_name}
              totalAudited={provider.provider_summary.total_audited}
              cqScore={provider.provider_summary.cq_score}
            />
          ) : (
            <ProviderSummaryPanel
              summary={provider.provider_summary}
              providerName={provider.provider_name}
            />
          )}
        </Box>
      </Box>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

// ─── Hook + Standalone viewer — unchanged ─────────────────────────────────────

export const useAuditReportAttachment = ({ batchId, chartId, providerId } = {}) => {
  const { data, loading, error, refresh } = useAuditReport({ batchId, chartId, providerId });
  const attachment = useMemo(() => ({
    attachment_type: 'AUDIT_REPORT',
    file: {
      name: `Audit Report${batchId ? ` — Batch ${batchId}` : chartId ? ` — File ${chartId}` : ''}`,
      mime_type: AUDIT_REPORT_MIME,
      url: 'audit-report://local',
      size: null,
      uploaded_at: null,
      uploaded_by: null,
      _auditReportData:    data,
      _auditReportLoading: loading,
      _auditReportError:   error,
      _auditReportRefresh: refresh,
    },
  }), [data, loading, error, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  return { attachment, loading, error, refresh };
};

const AuditReportViewer = ({ batchId, chartId, providerId }) => {
  const { data, loading, error, refresh } = useAuditReport({ batchId, chartId, providerId });
  return <AuditReportContent data={data} loading={loading} error={error} onRefresh={refresh} />;
};

export default AuditReportViewer;